import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import AnnualFortuneGrid, { generateMonthData } from './components/AnnualFortuneGrid';
import LuckyDayCalculator from './components/LuckyDayCalculator';
import { useBirthInfo } from '../../contexts/BirthInfoContext';
import { calculateFourPillars, analyzeElements, calculateElementBalance } from '../../utils/fourPillars';
import { usePdfDownload } from '../../hooks/usePdfDownload';
import SEOSchema from '../../components/feature/SEOSchema';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const annualReportSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  '@id': `${siteUrl}/annual-report`,
  name: '1년 운세 리포트 — 월별 사주 운세 분석',
  description: '사주팔자와 년운 기운을 바탕으로 1년 12개월 월별 운세를 상세 분석합니다. 재물운·연애운·건강운·직업운을 포함한 종합 운세 리포트를 PDF로 저장할 수 있습니다.',
  url: `${siteUrl}/annual-report`,
  provider: {
    '@type': 'Organization',
    name: 'DESTINY GPT',
    url: siteUrl,
  },
  serviceType: '연간 운세 리포트',
  areaServed: { '@type': 'Country', name: '대한민국' },
  inLanguage: 'ko-KR',
};

const YEAR_STEM_KO: Record<number, { stem: string; branch: string; element: string; label: string; overview: string }> = {
  2024: { stem: '갑', branch: '진', element: '목', label: '갑진년(甲辰年)', overview: '청룡의 해 — 새로운 시작과 강력한 도약의 기운이 충만한 변화의 해입니다.' },
  2025: { stem: '을', branch: '사', element: '목', label: '을사년(乙巳年)', overview: '푸른 뱀의 해 — 지혜와 변화, 재물의 기운이 활발하게 움직이는 해입니다.' },
  2026: { stem: '병', branch: '오', element: '화', label: '병오년(丙午年)', overview: '붉은 말의 해 — 열정과 도전, 화(火)의 강렬한 에너지로 성취와 명예가 빛나는 해입니다.' },
  2027: { stem: '정', branch: '미', element: '화', label: '정미년(丁未年)', overview: '붉은 양의 해 — 섬세하고 감성적인 기운으로 창의성과 인간관계가 꽃피는 해입니다.' },
  2028: { stem: '무', branch: '신', element: '토', label: '무신년(戊申年)', overview: '황금 원숭이의 해 — 안정적인 토(土) 기운 위에 금(金)의 결실이 함께하는 수확의 해입니다.' },
};

const LUCKY_ITEMS: Record<string, { color: string; direction: string; number: string; gem: string }> = {
  목: { color: '초록·청색', direction: '동쪽(東)', number: '3, 8', gem: '에메랄드·비취' },
  화: { color: '붉은·오렌지', direction: '남쪽(南)', number: '2, 7', gem: '루비·홍수정' },
  토: { color: '노란·황토색', direction: '중앙(中)', number: '5, 0', gem: '황수정·토파즈' },
  금: { color: '흰·금색', direction: '서쪽(西)', number: '4, 9', gem: '다이아몬드·백수정' },
  수: { color: '검은·파란', direction: '북쪽(北)', number: '1, 6', gem: '사파이어·흑수정' },
};

interface MonthDataLite {
  month: number;
  label: string;
  overall: number;
  wealth: number;
  love: number;
  career: number;
  health: number;
  relation: number;
  keyword: string;
  advice: string;
  luckyColor: string;
}

export default function AnnualReportPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { birthInfo } = useBirthInfo();
  const { downloadAnnualPdf } = usePdfDownload();
  const [isGenerating, setIsGenerating] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<MonthDataLite | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const luckyDaySectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (birthInfo) {
      setIsGenerating(true);
      const t = setTimeout(() => { setIsGenerating(false); setIsReady(true); }, 2000);
      return () => clearTimeout(t);
    }
  }, [birthInfo, selectedYear]);

  // Scroll to lucky day calculator if opened from destiny mode
  useEffect(() => {
    const eventParam = searchParams.get('event');
    if (eventParam && isReady && luckyDaySectionRef.current) {
      setTimeout(() => {
        luckyDaySectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 400);
    }
  }, [isReady, searchParams]);

  const handleDownloadPdf = () => {
    if (!birthInfo) return;
    setIsDownloading(true);
    const months = generateMonthData(birthInfo.birthDate, selectedYear);
    downloadAnnualPdf({
      birthInfo,
      year: selectedYear,
      dayStem: fp.day.stem,
      yearLabel: yearInfo.label,
      yearOverview: yearInfo.overview,
      compatScore: compat.score,
      compatMsg: compat.msg,
      luckyItems,
      months,
    });
    setTimeout(() => setIsDownloading(false), 2000);
  };

  if (!birthInfo) {
    return (
      <div className="min-h-screen bg-cosmic-gradient">
        <SEOSchema schema={annualReportSchema} id="annual-report-schema" />
        <Navbar />
        <main className="pt-20 min-h-[80vh] flex items-center justify-center px-4">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-primary-gold/20 to-amber-600/10 border border-primary-gold/30 flex items-center justify-center">
              <i className="ri-calendar-2-line text-primary-gold text-4xl"></i>
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">생년월일 정보가 필요합니다</h2>
            <p className="text-purple-300 mb-6 text-sm">1년 운세 리포트를 생성하려면 먼저 생년월일을 입력해주세요.</p>
            <button onClick={() => navigate('/')} className="px-6 py-3 bg-gradient-to-r from-primary-gold to-amber-500 rounded-full text-black font-bold hover:shadow-lg transition-all whitespace-nowrap cursor-pointer">
              정보 입력하러 가기
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const fp = calculateFourPillars(birthInfo.birthDate, birthInfo.birthTime);
  const balance = calculateElementBalance(fp);
  const analysis = analyzeElements(balance);
  const yearInfo = YEAR_STEM_KO[selectedYear] ?? YEAR_STEM_KO[2026];
  const luckyItems = LUCKY_ITEMS[yearInfo.element] ?? LUCKY_ITEMS['화'];
  const birthYear = new Date(birthInfo.birthDate).getFullYear();
  const age = selectedYear - birthYear;

  const elementCompat: Record<string, Record<string, { score: number; msg: string }>> = {
    목: { 목: { score: 4, msg: '동기 상생 — 같은 기운끼리 힘을 합치는 해' }, 화: { score: 5, msg: '목생화(木生火) — 내 기운이 화를 생해 에너지 폭발의 해' }, 토: { score: 3, msg: '목극토(木剋土) — 내가 주도하는 해, 과욕 주의' }, 금: { score: 2, msg: '금극목(金剋木) — 압박과 장애가 많은 해, 인내 필요' }, 수: { score: 4, msg: '수생목(水生木) — 귀인의 도움과 지원이 넘치는 해' } },
    화: { 목: { score: 4, msg: '목생화(木生火) — 지원과 도움이 풍부한 길한 해' }, 화: { score: 4, msg: '동기 상생 — 열정과 에너지가 가득한 해' }, 토: { score: 5, msg: '화생토(火生土) — 내 기운이 토를 생해 결실이 풍성한 해' }, 금: { score: 3, msg: '화극금(火剋金) — 내가 주도하되 충돌도 많은 해' }, 수: { score: 2, msg: '수극화(水剋火) — 방해와 제동이 많은 해, 신중 필요' } },
    토: { 목: { score: 2, msg: '목극토(木剋土) — 도전과 마찰이 많은 해, 유연성 필요' }, 화: { score: 4, msg: '화생토(火生土) — 귀인과 기회가 넘치는 길한 해' }, 토: { score: 4, msg: '동기 상생 — 안정과 신뢰가 쌓이는 해' }, 금: { score: 5, msg: '토생금(土生金) — 내 기운이 금을 생해 재물과 명예의 해' }, 수: { score: 3, msg: '토극수(土剋水) — 내가 통제하는 해, 책임감 증가' } },
    금: { 목: { score: 3, msg: '금극목(金剋木) — 성취하되 관계 마찰 주의' }, 화: { score: 2, msg: '화극금(火剋金) — 시련과 압박이 많은 해, 인내 필요' }, 토: { score: 4, msg: '토생금(土生金) — 기반이 탄탄해지는 안정의 해' }, 금: { score: 4, msg: '동기 상생 — 결단력이 빛나는 성취의 해' }, 수: { score: 5, msg: '금생수(金生水) — 내 기운이 수를 생해 지혜와 재물의 해' } },
    수: { 목: { score: 5, msg: '수생목(水生木) — 내 기운이 목을 생해 성장 폭발의 해' }, 화: { score: 3, msg: '수극화(水剋火) — 내가 리드하는 해, 과신 주의' }, 토: { score: 2, msg: '토극수(土剋水) — 제약과 장애가 많은 해, 인내 필요' }, 금: { score: 4, msg: '금생수(金生水) — 든든한 후원과 지원이 있는 해' }, 수: { score: 4, msg: '동기 상생 — 지혜와 직관이 극대화되는 해' } },
  };

  const dayStemEl = ({ 갑: '목', 을: '목', 병: '화', 정: '화', 무: '토', 기: '토', 경: '금', 신: '금', 임: '수', 계: '수' } as Record<string, string>)[fp.day.stem] ?? '목';
  const compat = elementCompat[dayStemEl]?.[yearInfo.element] ?? { score: 3, msg: '균형 잡힌 한 해입니다.' };

  const years = [currentYear - 1, currentYear, currentYear + 1, currentYear + 2];

  return (
    <div className="min-h-screen bg-cosmic-gradient">
      <SEOSchema schema={annualReportSchema} id="annual-report-schema" />
      <Navbar />
      <main className="pt-24 sm:pt-28">
        {/* Hero */}
        <section className="relative pb-8 px-4 sm:px-6">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <img
              src="https://public.readdy.ai/ai/img_res/87f0efac8042a0f79abf6eb2b6e35650.jpg"
              alt="annual report hero"
              className="w-full h-full object-cover object-top opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-primary-dark/50 via-primary-dark/70 to-primary-dark"></div>
          </div>
          <div className="relative z-10 max-w-4xl mx-auto text-center pt-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-gold/15 border border-primary-gold/40 mb-5">
              <i className="ri-calendar-2-line text-primary-gold text-sm"></i>
              <span className="text-primary-gold text-xs font-bold tracking-widest uppercase">1년 운세 리포트</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-black text-white mb-4 leading-tight">
              {selectedYear}년<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-gold via-amber-300 to-primary-gold">
                {birthInfo.name ? birthInfo.name + '님의' : '당신의'} 운세
              </span>
            </h1>
            <p className="text-purple-300/80 text-base mb-6">{yearInfo.label} · {yearInfo.overview}</p>

            {/* Year selector */}
            <div className="flex items-center justify-center gap-2 mb-6">
              {years.map((y) => (
                <button
                  key={y}
                  onClick={() => { setSelectedYear(y); setIsReady(false); setIsGenerating(true); setTimeout(() => { setIsGenerating(false); setIsReady(true); }, 1500); }}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap border ${
                    y === selectedYear
                      ? 'bg-primary-gold text-black border-primary-gold'
                      : 'bg-white/5 border-white/10 text-purple-300 hover:border-primary-gold/40'
                  }`}
                >
                  {y}년
                </button>
              ))}
            </div>

            {/* Quick stats */}
            <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto">
              <div className="glass-card rounded-2xl p-3 text-center">
                <p className="text-primary-gold font-black text-xl">{age}세</p>
                <p className="text-purple-400/60 text-xs">나이</p>
              </div>
              <div className="glass-card rounded-2xl p-3 text-center">
                <p className="text-primary-gold font-black text-xl">{fp.day.stem}{fp.day.branch}</p>
                <p className="text-purple-400/60 text-xs">일주(日柱)</p>
              </div>
              <div className="glass-card rounded-2xl p-3 text-center">
                <div className={`font-black text-xl ${compat.score >= 4 ? 'text-amber-400' : compat.score >= 3 ? 'text-purple-300' : 'text-orange-400'}`}>
                  {'★'.repeat(compat.score)}{'☆'.repeat(5 - compat.score)}
                </div>
                <p className="text-purple-400/60 text-xs">년운 궁합</p>
              </div>
            </div>
          </div>
        </section>

        {isGenerating && (
          <div className="max-w-3xl mx-auto px-4 py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-6 relative">
              <div className="absolute inset-0 rounded-full border-4 border-primary-gold/30"></div>
              <div className="absolute inset-0 rounded-full border-4 border-primary-gold border-t-transparent animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <i className="ri-calendar-2-line text-primary-gold text-3xl"></i>
              </div>
            </div>
            <h3 className="text-xl font-bold text-white mb-2">{selectedYear}년 운세를 분석 중입니다...</h3>
            <p className="text-purple-300/60 text-sm">사주 오행과 년운 기운을 계산하고 있어요</p>
          </div>
        )}

        {isReady && (
          <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-20 space-y-10">
            {/* PDF Download Button */}
            <div className="flex justify-end">
              <button
                onClick={handleDownloadPdf}
                disabled={isDownloading}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/5 border border-white/15 text-purple-200 hover:border-primary-gold/40 hover:text-primary-gold transition-all text-sm font-semibold cursor-pointer whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  {isDownloading
                    ? <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                    : <i className="ri-download-2-line text-sm"></i>
                  }
                </div>
                <span>{isDownloading ? 'PDF 준비 중...' : 'PDF 저장'}</span>
              </button>
            </div>

            {/* Year compatibility card */}
            <div className={`p-6 rounded-2xl border ${compat.score >= 4 ? 'bg-amber-500/10 border-amber-500/30' : compat.score >= 3 ? 'bg-purple-500/10 border-purple-500/30' : 'bg-orange-500/10 border-orange-500/30'}`}>
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 flex items-center justify-center rounded-2xl flex-shrink-0 ${compat.score >= 4 ? 'bg-amber-500/20' : compat.score >= 3 ? 'bg-purple-500/20' : 'bg-orange-500/20'}`}>
                  <i className={`ri-yin-yang-line text-xl ${compat.score >= 4 ? 'text-amber-400' : compat.score >= 3 ? 'text-purple-300' : 'text-orange-400'}`}></i>
                </div>
                <div className="flex-1">
                  <p className="text-white font-bold text-base mb-1">{selectedYear}년 — {fp.day.stem}일간 × {yearInfo.element}(年) 기운 궁합</p>
                  <p className={`text-sm font-semibold mb-2 ${compat.score >= 4 ? 'text-amber-400' : compat.score >= 3 ? 'text-purple-300' : 'text-orange-400'}`}>{compat.msg}</p>
                  <p className="text-purple-300/70 text-sm">강점 오행: {analysis.strongest} · 보완 오행: {analysis.weakest}</p>
                </div>
                <div className="flex-shrink-0 text-right">
                  <p className={`font-black text-2xl ${compat.score >= 4 ? 'text-amber-400' : compat.score >= 3 ? 'text-purple-300' : 'text-orange-400'}`}>{compat.score}/5</p>
                  <p className="text-purple-400/60 text-xs">년운 궁합</p>
                </div>
              </div>
            </div>

            {/* Lucky items for year */}
            <div className="p-6 rounded-2xl bg-gradient-to-br from-primary-dark/80 to-mystic-dark/80 border border-primary-gold/20">
              <h2 className="text-white font-bold text-lg mb-5 flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-sparkling-2-fill text-primary-gold text-base"></i>
                </div>
                {selectedYear}년 행운 아이템
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { icon: 'ri-palette-line', label: '행운 컬러', value: luckyItems.color },
                  { icon: 'ri-compass-3-line', label: '길한 방향', value: luckyItems.direction },
                  { icon: 'ri-hashtag', label: '행운 숫자', value: luckyItems.number },
                  { icon: 'ri-gem-line', label: '행운 보석', value: luckyItems.gem },
                ].map((item) => (
                  <div key={item.label} className="p-4 rounded-xl bg-white/5 border border-white/8 text-center">
                    <div className="w-7 h-7 flex items-center justify-center mx-auto mb-2">
                      <i className={`${item.icon} text-primary-gold text-base`}></i>
                    </div>
                    <p className="text-purple-300/60 text-xs mb-1">{item.label}</p>
                    <p className="text-white font-bold text-sm">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Annual fortune grid */}
            <div className="p-6 rounded-2xl bg-gradient-to-br from-primary-dark/80 to-mystic-dark/80 border border-primary-gold/20">
              <h2 className="text-white font-bold text-lg mb-6 flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-bar-chart-grouped-line text-primary-gold text-base"></i>
                </div>
                {selectedYear}년 월별 운세 분석
              </h2>
              <AnnualFortuneGrid
                birthDate={birthInfo.birthDate}
                birthTime={birthInfo.birthTime}
                name={birthInfo.name}
                dayStem={fp.day.stem}
                year={selectedYear}
                selectedMonth={selectedMonth}
                onMonthSelect={setSelectedMonth}
              />
            </div>

            {/* Lucky Day Calculator */}
            <div ref={luckyDaySectionRef}>
              <LuckyDayCalculator
                birthDate={birthInfo.birthDate}
                dayStem={fp.day.stem}
                year={selectedYear}
              />
            </div>

            {/* Link to lifetime report */}
            <div className="p-6 rounded-2xl bg-gradient-to-br from-amber-900/20 to-amber-800/10 border border-amber-500/25 text-center">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-2xl bg-amber-500/10 border border-amber-500/20">
                <i className="ri-infinity-line text-amber-400 text-2xl"></i>
              </div>
              <h3 className="text-white font-bold text-base mb-1">더 큰 그림이 궁금하신가요?</h3>
              <p className="text-purple-300/70 text-sm mb-4">평생 운세 리포트에서 인생 전체 사이클과 대운 흐름을 확인하세요</p>
              <Link to="/lifetime-report" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-600 text-black font-bold text-sm hover:shadow-lg hover:shadow-amber-500/30 transition-all whitespace-nowrap">
                <i className="ri-infinity-line text-sm"></i>
                <span>평생 운세 리포트 보기</span>
              </Link>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
